const VALIDATE_URL = '/api/validate-license';
const PRODUCTION_VALIDATE_URL = 'https://hub.indxone.com/api/validate-license';
const CACHE_PREFIX = 'indxone.license.cloud.v1:';
const CACHE_TTL_MS = 2 * 60 * 1000;

export interface LicenseValidationResult {
  ok: boolean;
  tier: 'free' | 'starter' | 'pro';
  status: 'ok' | 'expired' | 'invalid';
  expiresAt: string | null;
  expiresLabel: string | null;
  kind?: string | null;
  source: 'cloud' | 'offline-cache' | 'local-fallback' | string;
  message: string;
}

function getCacheKey(key: string): string {
  return `${CACHE_PREFIX}${encodeURIComponent(key.trim())}`;
}

function getCachedResult(key: string): LicenseValidationResult | null {
  try {
    const raw = localStorage.getItem(getCacheKey(key));
    if (!raw) return null;
    const cached = JSON.parse(raw) as LicenseValidationResult & { _cachedAt: number };
    if (Date.now() - cached._cachedAt > CACHE_TTL_MS) {
      localStorage.removeItem(getCacheKey(key));
      return null;
    }
    return { ...cached, source: 'offline-cache' };
  } catch {
    return null;
  }
}

function setCachedResult(key: string, result: LicenseValidationResult): void {
  try {
    localStorage.setItem(getCacheKey(key), JSON.stringify({ ...result, _cachedAt: Date.now() }));
  } catch {
    /* noop */
  }
}

function clearCachedResult(key: string): void {
  try {
    localStorage.removeItem(getCacheKey(key));
  } catch {
    /* noop */
  }
}

export function resolveLicenseValidationUrl(hostname?: string): string {
  const currentHostname = hostname ?? (typeof window !== 'undefined' ? window.location.hostname : '');
  if (currentHostname === 'localhost' || currentHostname === '127.0.0.1') {
    return PRODUCTION_VALIDATE_URL;
  }
  return VALIDATE_URL;
}

function buildFallbackResult(): LicenseValidationResult {
  return {
    ok: false,
    tier: 'free',
    status: 'invalid',
    expiresAt: null,
    expiresLabel: null,
    source: 'local-fallback',
    message: 'Validation cloud indisponible. Réessaie plus tard.',
  };
}

function normalizeResult(value: unknown): LicenseValidationResult {
  const result = value as Partial<LicenseValidationResult> | null;
  const tier = result?.tier === 'starter' || result?.tier === 'pro' ? result.tier : 'free';
  const status = result?.status === 'ok' || result?.status === 'expired' || result?.status === 'invalid'
    ? result.status
    : result?.ok
      ? 'ok'
      : 'invalid';

  return {
    ok: result?.ok === true,
    tier,
    status,
    expiresAt: typeof result?.expiresAt === 'string' ? result.expiresAt : null,
    expiresLabel: typeof result?.expiresLabel === 'string' ? result.expiresLabel : null,
    kind: typeof result?.kind === 'string' ? result.kind : null,
    source: result?.source || 'cloud',
    message: result?.message || (result?.ok ? 'Licence valide.' : 'Licence invalide.'),
  };
}

export async function validateLicenseCloud(key: string): Promise<LicenseValidationResult> {
  if (!key || typeof key !== 'string') {
    return buildFallbackResult();
  }

  try {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 3000);

    const response = await fetch(resolveLicenseValidationUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ key }),
      signal: controller.signal,
    }).finally(() => clearTimeout(timeout));

    if (!response.ok) {
      // HTTP error — server responded, don't use cache, server is authority
      clearCachedResult(key);
      return normalizeResult({ ok: false, status: 'invalid', message: 'Licence invalide.', source: 'cloud' });
    }

    const result = normalizeResult(await response.json());
    if (result.ok) {
      setCachedResult(key, result);
    } else {
      clearCachedResult(key);
    }
    return result;
  } catch {
    // Network error (timeout, abort, etc.) — use cache if available
    const cached = getCachedResult(key);
    if (cached?.ok) return cached;
    return buildFallbackResult();
  }
}
